import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchLeadStatusesAsync } from "./store/slices/leadStatusSlice";
import { Layout } from "./components/layout/Layout";
import { Dashboard } from "./pages/Dashboard";
import { Leads } from "./pages/Leads";
import { LiveLeads } from "./pages/LiveLeads";
import { ViewLead } from "./pages/ViewLead";
import { Staff } from "./pages/Staff";
import { CreateStaff } from "./pages/CreateStaff";
import { CreateLead } from "./pages/CreateLead";
import { Desks } from "./pages/Desks";
import { AgentProfile } from "./pages/AgentProfile";
import { ViewProfile } from "./pages/ViewProfile";
import { EditProfile } from "./pages/EditProfile";
import { Login } from "./pages/Login";
import { FastLogin } from "./pages/FastLogin";
import { LeadStatusManagement } from "./pages/settings/LeadStatusManagement";
import { CustomPositionManagement } from "./pages/settings/CustomPositionManagement";
import { Documentation } from "./pages/Documentation";
import NotFound from "./pages/NotFound";
import { SystemSettings } from "./pages/settings/SystemSettings";
import { ExportAuditLog } from "./pages/settings/ExportAuditLog";
import { IncomingDuplicates } from "./pages/settings/IncomingDuplicates";
import { AIAssistant } from "./pages/AIAssistant";
import { AiCaller } from "./pages/AiCaller";
import { AiAgents } from "./pages/AiAgents";
import EmailTemplates from "./pages/settings/EmailTemplates";
import { MyWork } from "./pages/MyWork";
import { WorkTracking } from "./pages/settings/WorkTracking";
import { Inbox } from "./pages/Inbox";
import { Calendar as CalendarPage } from "./pages/Calendar";
import { Games } from "./pages/Games";
import { AuthProvider, useAuth } from "./hooks/useAuth";

const queryClient = new QueryClient();

// Component to initialize data
const DataInitializer = () => {
    const dispatch = useDispatch();

    const { user, isAuthenticated } = useAuth();

    useEffect(() => {
        // Only fetch data when user is authenticated
        if (isAuthenticated && user) {
            // Fetch lead statuses on app initialization
            dispatch(fetchLeadStatusesAsync());
        }
    }, [dispatch, isAuthenticated, user]);

    return null;
};

const App = () => (
    <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <TooltipProvider>
                        <DataInitializer />
                        <Toaster />
                        <Sonner />
                        <BrowserRouter>
                            <Routes>
                                {/* Public routes */}
                                <Route path="/login" element={<Login />} />
                                <Route path="/fast-login" element={<FastLogin />} />

                                {/* Protected routes */}
                                <Route
                                    path="/"
                                    element={
                                        <Layout>
                                            <Dashboard />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/leads"
                                    element={
                                        <Layout>
                                            <Leads />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/live-leads"
                                    element={
                                        <Layout>
                                            <LiveLeads />
                                        </Layout>
                                    }
                                />
                                {/* <Route
                                    path="/leads/view/:_id"
                                    element={
                                        <Layout>
                                            <ViewLead />
                                        </Layout>
                                    }
                                /> */}
                                <Route
                                    path="/leads/create"
                                    element={
                                        <Layout>
                                            <CreateLead />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/leads/edit/:_id"
                                    element={
                                        <Layout>
                                            <CreateLead />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/staff"
                                    element={
                                        <Layout>
                                            <Staff />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/staff/create"
                                    element={
                                        <Layout>
                                            <CreateStaff />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/staff/edit/:_id"
                                    element={
                                        <Layout>
                                            <CreateStaff />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/desks"
                                    element={
                                        <Layout>
                                            <Desks />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/agent/:agentId"
                                    element={
                                        <Layout>
                                            <AgentProfile />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/profile"
                                    element={
                                        <Layout>
                                            <ViewProfile />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/profile/edit"
                                    element={
                                        <Layout>
                                            <EditProfile />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/lead-status"
                                    element={
                                        <Layout>
                                            <LeadStatusManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/positions"
                                    element={
                                        <Layout>
                                            <CustomPositionManagement />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/system"
                                    element={
                                        <Layout>
                                            <SystemSettings />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/export-audit"
                                    element={
                                        <Layout>
                                            <ExportAuditLog />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/incoming-duplicates"
                                    element={
                                        <Layout>
                                            <IncomingDuplicates />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/documentation"
                                    element={
                                        <Layout>
                                            <Documentation />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/ai-assistant"
                                    element={
                                        <Layout>
                                            <AIAssistant />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/ai-caller"
                                    element={
                                        <Layout>
                                            <AiCaller />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/ai-agents"
                                    element={
                                        <Layout>
                                            <AiAgents />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/email-templates"
                                    element={
                                        <Layout>
                                            <EmailTemplates />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/my-work"
                                    element={
                                        <Layout>
                                            <MyWork />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/settings/work-tracking"
                                    element={
                                        <Layout>
                                            <WorkTracking />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/inbox"
                                    element={
                                        <Layout>
                                            <Inbox />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/calendar"
                                    element={
                                        <Layout>
                                            <CalendarPage />
                                        </Layout>
                                    }
                                />
                                <Route
                                    path="/games"
                                    element={
                                        <Layout>
                                            <Games />
                                        </Layout>
                                    }
                                />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </BrowserRouter>
                    </TooltipProvider>
                </AuthProvider>
            </QueryClientProvider>
        </PersistGate>
    </Provider>
);

export default App;
